import { BaseListViewStore, BooleanToggleCellData } from 'core/stores';
import { observable, action, makeObservable } from 'mobx';
import { checkForColumnWidth, isSeller, isSuperAdminOrganization, isWarehouse, notificationError } from 'core/utils';
import { localStorageHandler } from 'core/services';
import ReturnAddressStore from './ReturnAddressStore';
import { sourceOfChange } from 'common/constants';
import _ from 'lodash';
import { ReturnAddressEdit } from '../pages';

class ReturnAddressListViewStore extends BaseListViewStore {
	@observable selectedItem = null;
	isSuperAdmin = false;
	localStorageWidthKey = 'return-address-column-width';
	isEdit = false;

	constructor(rootStore) {
		super(rootStore, {
			listStore: new ReturnAddressStore(rootStore),
			queryParams: {
				embed: ['seller', 'address', 'address.coreCountry', 'address.coreCountryState'],
			},
			findFunction: 'findReturnAddress',
			tableData: {
				columns: [
					{
						key: 'name',
						title: 'RETURNADDRESSVIEW.NAME',
						sortable: true,
						selected: true,
					},
					{
						key: 'fullAddress',
						title: 'RETURNADDRESSVIEW.RETURNADDRESS',
						sortable: false,
						selected: true,
					},
					{
						key: 'isDefaultReturnAddress',
						title: 'RETURNADDRESSVIEW.ISDEFAULTRETURNADDRESS',
						dataCell: BooleanToggleCellData,
						selected: true,
						sortable: false,
						onClick: returnAddress => this.setToDefaultReturnAddress(returnAddress),
					},
					{
						key: 'seller.applicationTitle',
						title: 'RETURNADDRESSVIEW.SELLER',
						sortable: false,
						selected: true,
						enabled: isSuperAdminOrganization(rootStore),
					},
				],
				actions: {
					onReturnAddressEdit: returnAddress => this.openEditModal(returnAddress),
					onReturnAddressDelete: item => this.openConfirmationModal(this.getDeleteConfirmModalProps(item)),
				},
				sortObject: [],
			},
			routes: {
				create: () => {
					this.openEditModal();
				},
			},
			permissions: {
				onReturnAddressEdit: () => this.authStore.hasPermission(''),
				onReturnAddressDelete: () => this.authStore.hasPermission(''),
			},
		});
		makeObservable(this);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		if (isSuperAdminOrganization(this.rootStore)) {
			this.isSuperAdmin = true;
		}
		if (!isSeller(this.rootStore)) {
			await this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		await this.queryUtility.initialize(this.findReturnAddress);
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.setHeaderModes();
	};
	@action.bound
	openEditModal(item) {
		this.isEdit = item != null;
		this.openModal({
			onSuccess: async () => {
				this.isEdit = false;
				await this.findReturnAddress();
			},
			data: { ...this, selectedItem: item },
			component: ReturnAddressEdit,
		});
	}

	@action.bound
	setToDefaultReturnAddress = async returnAddress => {
		let setDefault;
		if (returnAddress.isDefaultReturnAddress) {
			returnAddress.isDefaultReturnAddress = false;
			setDefault = 'RETURNADDRESSVIEW.UNSET';
		} else {
			returnAddress.isDefaultReturnAddress = true;
			setDefault = 'RETURNADDRESSVIEW.SET';
		}
		try {
			await this.listStore.updateReturnAddress(returnAddress, sourceOfChange.updateReturnAddress);
			this.rootStore.notificationStore.success([
				'RETURNADDRESSVIEW.SETDEFAULTSUCCESMESSAGE',
				{ blank: setDefault, name: returnAddress.name },
			]);
			await this.findReturnAddress();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	deleteReturnAddress = async returnAddress => {
		this.suspend();
		try {
			await this.listStore.deleteReturnAddress(returnAddress.id);
			this.rootStore.notificationStore.success(['RETURNADDRESSVIEW.DELETESUCCESS', { name: returnAddress.name }]);
			await this.findReturnAddress();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	findReturnAddress = async () => {
		this.suspend();
		this.isEdit = false;
		this.setButtons();
		this.queryUtility.setFilterParams(this, null, this.tableFilter);
		await this.listStore.findReturnAddress(this.queryUtility.filter);

		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		this.resume();
	};

	@action.bound
	setButtons() {
		this.setButtonComponent({
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-plus spc--right--sml',
					onClick: this.routes.create,
					authorization: 'channelSection.create',
					text: 'RETURNADDRESSNEW',
				},
			],
		});
	}

	getDeleteConfirmModalProps = item => {
		return {
			onConfirm: async () => this.deleteReturnAddress(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['RETURNADDRESSVIEW.CONFIRMDELETE', { name: item.name }],
			data: item,
		};
	};
}

export default ReturnAddressListViewStore;
